<template>
  <div
    v-if="hasChildren || pageType !== 'node'"
    :class="[
      'border-gray-dark flex flex-col border-t',
      { 'last:border-b': depth === 0 },
    ]"
  >
    <NuxtLink
      v-if="!hasChildren && href"
      :to="href"
      :target="isExternal ? '_blank' : '_self'"
      :rel="isExternal ? 'noopener noreferrer' : undefined"
      class="group flex items-center justify-between border-b border-transparent py-2.5 transition-colors duration-300 md:pt-3 md:pb-2.5"
      @click="emit('close')"
    >
      <span
        :class="[
          'bp1680:text-[23px]/[29px] text-[16px]/[19px] transition-colors duration-300 select-none md:text-[17px]/[21px] xl:text-[20px]/[24px] 2xl:text-[23px]/[29px]',
          isCurrentPage
            ? 'text-white underline decoration-1 underline-offset-5'
            : 'group-hover:text-gray text-white',
        ]"
      >
        {{ node.label }}
      </span>

      <span
        v-if="node.type == 'external'"
        class="group-hover:text-gray relative flex h-4 w-4 shrink-0 items-center justify-center text-white transition-colors duration-300 xl:h-5 xl:w-5"
      >
        <ArrowIcon class="h-3.5 w-3.5 xl:h-4 xl:w-4" />
      </span>
    </NuxtLink>

    <button
      v-else-if="hasChildren"
      type="button"
      :class="[
        'group flex w-full cursor-pointer items-center justify-between border-b py-2.5 text-left transition-colors duration-300 md:pt-3 md:pb-2.5',
        isOpen ? 'border-gray-dark' : 'border-transparent',
      ]"
      :aria-expanded="isOpen"
      @click="handleClick"
    >
      <span
        :class="[
          'bp1680:text-[23px]/[29px] text-[16px]/[19px] transition-colors duration-300 select-none md:text-[17px]/[21px] xl:text-[20px]/[24px] 2xl:text-[23px]/[29px]',
          isCurrentPage
            ? 'text-white underline decoration-1 underline-offset-5'
            : 'group-hover:text-gray text-white',
        ]"
      >
        {{ node.label }}
      </span>
      <span
        class="group-hover:text-gray relative flex h-4 w-4 shrink-0 items-center justify-center text-white transition-colors duration-300 xl:h-5 xl:w-5"
      >
        <Transition name="icon-switch">
          <MinusIcon v-if="isOpen" class="absolute h-4 w-4 xl:h-5 xl:w-5" />
          <PlusIcon v-else class="absolute h-4 w-4 xl:h-5 xl:w-5" />
        </Transition>
      </span>
    </button>

    <div
      v-if="hasChildren"
      :inert="!isOpen || undefined"
      :style="{ transitionDuration: dynamicDuration }"
      :class="[
        'grid transition-all ease-in-out',
        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
      ]"
    >
      <div
        class="flex flex-col overflow-hidden pl-5 md:pl-10 [&>*:first-child]:border-t-0"
      >
        <NavigationNode
          v-for="child in node.children"
          :key="child.id"
          :node="child"
          :depth="depth + 1"
          :page-type="child.type"
          @close="emit('close')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { MenuItem } from '~/types'
import ArrowIcon from './icons/ArrowIcon.vue'

interface Props {
  node: MenuItem
  depth?: number
  pageType: string
}

const props = withDefaults(defineProps<Props>(), {
  depth: 0,
})

const emit = defineEmits(['close'])

const route = useRoute()
const localePath = useLocalePath()

const hasChildren = computed(
  () => !!props.node?.children && props.node.children.length > 0,
)

const isExternal = computed(() => props.node.type === 'external')

const href = computed(() => {
  const raw = props.node.url || props.node.link
  if (!raw) return null
  if (isExternal.value) return raw
  return localePath(raw)
})

const isCurrentPage = computed(() => {
  return href.value && route.path === href.value
})

const isAncestorOfCurrent = computed(() => {
  if (!href.value) return false
  return route.path.startsWith(href.value + '/')
})

const isOpen = ref(false)

onMounted(() => {
  if (hasChildren.value && isAncestorOfCurrent.value) {
    isOpen.value = true
  }
})

const dynamicDuration = computed(() => {
  if (!hasChildren.value) return '300ms'

  const childCount = props.node.children!.length

  const calculatedTime = 300 + childCount * 40
  const duration = Math.min(calculatedTime, 800)

  return `${duration}ms`
})

const handleClick = () => {
  if (hasChildren.value) {
    isOpen.value = !isOpen.value
  } else if (href.value) {
    if (isExternal.value) {
      window.open(
        href.value,
        props.node.target ?? '_blank',
        'noopener,noreferrer',
      )
    } else {
      navigateTo(href.value)
    }
    emit('close')
  }
}
</script>

<style scoped>
.icon-switch-enter-active,
.icon-switch-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.icon-switch-enter-from {
  opacity: 0;
  transform: rotate(-90deg) scale(0.5);
}

.icon-switch-leave-to {
  opacity: 0;
  transform: rotate(90deg) scale(0.5);
}
</style>
